import React, { useEffect, useState } from 'react';
import useCanonical from '../../helper/useCanonical';
import { constant } from '../../utils/constant';
import { Link, useParams } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import { formatTitleForURL } from '../../helper/FormatUrl';

const CategoryCard = () => {
  const [categoryData, setCategoryData] = useState([]);
  const [allCategory, setAllCategory] = useState([]);
  const [category, setCategory] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const { slug } = useParams();

  const fetchCategoryData = async () => {
    try {
      const response = await fetch(`${constant.CONST_SARCASM_BLOG_BY_CATEGORY}/${slug}`);
      const json = await response.json();
      // An empty category comes back as `success: false` / "No Record Found"
      // (with HTTP 200). Assign either way — bailing out on !success left the
      // PREVIOUS category's posts on screen when moving to an empty one.
      setCategoryData(json.success && Array.isArray(json.data) ? json.data : []);
    } catch (error) {
      console.error('Error fetching category data:', error);
      setCategoryData([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAllCategory = async () => {
    try {
      const response = await fetch(`${constant.CONST_SARCASM_BLOG_CATEGORY}?limit=500`);
      const result = await response.json();
      if (result.success) {
        setAllCategory(result.data);
      }
    } catch (error) {
      console.error('Error fetching all categories:', error);
    }
  };

  // The category itself, for its SEO tags. Fetched directly by slug rather than
  // picked out of fetchAllCategory's result, so it does not depend on that list
  // being paginated wide enough to contain it.
  const fetchCategory = async () => {
    try {
      const response = await fetch(`${constant.CONST_SARCASM_BLOG_CATEGORY}/${slug}`);
      const result = await response.json();
      // Same as above — a miss must clear the previous category, or its SEO
      // title would carry over to the next one.
      setCategory(result.success ? result.data : null);
    } catch (error) {
      console.error('Error fetching category:', error);
      setCategory(null);
    }
  };

  useEffect(() => {
    // Drop the outgoing category's data up front so nothing from it can show
    // through while the new one is still in flight.
    setIsLoading(true);
    setCategoryData([]);
    setCategory(null);
    fetchCategoryData();
    fetchAllCategory();
    fetchCategory();
  }, [slug]);

  // Was a <Helmet> block, which injected nothing — see helper/useCanonical.
  // Called above the early returns below: hooks cannot be conditional.
  useCanonical(
    `/categories/${slug}`,
    category?.metaTitle || category?.name,
    category?.metaDescription
  );

  const getCategoryName = (categoryId) => {
    const category = allCategory.find((cat) => cat._id === categoryId);
    return category ? category.name : 'Unknown Category';
  };

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', marginTop: '2rem' }}>
        <Spinner />
      </div>
    );
  }

  if (!isLoading && categoryData.length === 0) {
    return (
      <div style={{ textAlign: 'center', marginTop: '2rem' }}>
        <p>No Data Available</p>
      </div>
    );
  }

  return (
    <div style={{ margin: '1rem 0', padding: '2rem ' }}>
      <div className="row g-3">
        {categoryData.map((item) => (
          <div className="col-lg-4" key={item?._id}>
            <div className="h-100 post-box-layout4 box-border-dark-1 radius-default padding-20 bg-color-old-lace box-shadow-large shadow-style-2 transition-default">
              <div className="figure-holder radius-default">
                {item?.slug ? (
                  <Link to={`/blog/${item?.slug}`} className="link-wrap">
                    <img className="object-fit-cover w-100" height="250" src={item?.bannerImageUrl} alt="Post" />
                  </Link>
                ) : (
                  <img className="object-fit-cover w-100" height="250" src={item?.bannerImageUrl} alt="Post" />
                )}
              </div>
              <div className="content-holder">
                <div className="entry-category style-2 color-dark-1-fixed">
                  <ul>
                    <li>
                      {item?.slug ? (
                        <Link to={`/blog/${item?.slug}`}>{getCategoryName(item?.blogCategoryId)}</Link>
                      ) : (
                        <span>{getCategoryName(item?.blogCategoryId)}</span>
                      )}
                    </li>
                  </ul>
                </div>
                <h3 className="entry-title color-dark-1-fixed underline-animation">
                  {item?.slug ? (
                    <Link to={`/blog/${item?.slug}`} className="link-wrap">
                      {item?.title}
                    </Link>
                  ) : (
                    <span>{item?.title}</span>
                  )}
                </h3>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CategoryCard;
